/**
 * The subscription list: one pill per client site, saying which package it is
 * on and how it is being billed.
 *
 * This was a bordered `<table>` borrowed from `@wa/ui`, with shadcn badges in
 * the cells. A subscription row has a subject you open - the site - so the
 * design notes put it on pills: a table is for numbers you compare, a pill list
 * is for objects you click.
 */
import { CalendarClock, CreditCard, Layers, Loader2 } from "lucide-react";
import { formatDate, localeText, type Locale, type PlansT, type SubscriptionRow } from "@wa/ui";
import { Avatar, EmptyState, StatusPill, listCard, type StatusTone } from "../../ui";
import { SubsRowMenu } from "./SubsRowMenu";

/**
 * Site, owner, package, status, billing term, add-ons - six tracks plus the
 * 44px actions cell, used by BOTH the head and the rows so neither side can
 * gain a column without the other.
 *
 * The old table carried seven data columns: the billing cycle and the period
 * end each had one. They share a cell here (the cycle on the first line, the
 * date it runs to underneath), because seven tracks is the point past which a
 * list stops being readable and starts being a horizontal scroll.
 */
const COLS = "minmax(0,2.1fr) minmax(0,1.6fr) 1.3fr 1fr 1.15fr .55fr 44px";

/** Settled is green, a trial is informational, an unpaid invoice is late, and
 *  a subscription that has stopped (cancelled or paused) is simply quiet. */
const STATUS_TONE: Record<string, StatusTone> = {
  active: "ok",
  trialing: "info",
  past_due: "lt",
  canceled: "mut",
  paused: "mut",
};

export function SubsList({
  rows,
  loading,
  t,
  locale,
  onAssign,
  onRefund,
}: {
  rows: SubscriptionRow[];
  loading: boolean;
  t: PlansT;
  locale: Locale;
  onAssign: (row: SubscriptionRow) => void;
  onRefund: (row: SubscriptionRow) => void;
}) {
  return (
    <div className="crm-card" style={listCard(COLS)}>
      {loading ? (
        <div className="flex justify-center py-16 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState icon={Layers} title={t("subs.empty")} />
      ) : (
        <>
          <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 22 }}>
            <span>{t("subs.col.site")}</span>
            <span>{t("subs.col.owner")}</span>
            <span>{t("subs.col.plan")}</span>
            <span>{t("subs.col.status")}</span>
            <span>{t("subs.col.cycle")}</span>
            <span>{t("subs.col.addons")}</span>
            <span />
          </div>

          <div className="crm-rows pt-0">
            {rows.map((row) => (
              <SubsRow
                key={row.siteId}
                row={row}
                t={t}
                locale={locale}
                onAssign={onAssign}
                onRefund={onRefund}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SubsRow({
  row,
  t,
  locale,
  onAssign,
  onRefund,
}: {
  row: SubscriptionRow;
  t: PlansT;
  locale: Locale;
  onAssign: (row: SubscriptionRow) => void;
  onRefund: (row: SubscriptionRow) => void;
}) {
  return (
    // A div rather than a link: the row ends in a menu button, and there is no
    // site page in this app to link to anyway. The subject is a real <button>
    // stretching an ::after over the whole row, so the row opens end to end from
    // the keyboard as well as the mouse, and the menu still sits on top of it.
    <div className="crm-list-row relative" style={{ gridTemplateColumns: COLS }}>
      <span className="flex min-w-0 items-center gap-3">
        <Avatar name={row.siteName} />
        <span className="min-w-0">
          <button
            type="button"
            onClick={() => onAssign(row)}
            className="crm-prow-n crm-trunc block w-full cursor-pointer bg-transparent p-0 text-left after:absolute after:inset-0 after:rounded-full"
          >
            {row.siteName}
          </button>
          <span className="crm-prow-s crm-trunc block">{row.siteSlug}</span>
        </span>
      </span>

      <span className="min-w-0">
        {row.owner ? (
          <>
            <span className="crm-trunc block text-[12.5px]">{row.owner.name}</span>
            <span className="crm-prow-s crm-trunc block">{row.owner.email}</span>
          </>
        ) : (
          <span className="crm-mut text-[12.5px]">{t("subs.noOwner")}</span>
        )}
      </span>

      <span className="min-w-0">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="crm-trunc text-[12.5px]">{localeText(row.planName, locale) || row.planCode}</span>
          {/* Which gateway the money actually comes through. A row without it is
              billed by hand, which is the whole reason the assign dialog exists. */}
          {row.provider === "stripe" ? (
            <span className="crm-chip shrink-0">
              <CreditCard className="size-3" />
              {t("subs.stripe.badge")}
            </span>
          ) : null}
        </span>
        {/* No subscription record at all: the site falls back to the free plan
            rather than having been put on it, and that difference decides
            whether there is anything to refund or cancel. */}
        {!row.hasSubscription ? <span className="crm-prow-s crm-trunc block">{t("subs.implicitFree")}</span> : null}
      </span>

      <span>
        <StatusPill tone={STATUS_TONE[row.status] ?? "mut"}>{t(`status.${row.status}`)}</StatusPill>
      </span>

      <span className="min-w-0">
        <span className="crm-trunc block text-[12.5px]">{t(`cycle.${row.billingCycle}`)}</span>
        <span className="crm-prow-s crm-tnum flex items-center gap-1.5" title={t("subs.col.periodEnd")}>
          <CalendarClock className="size-3 shrink-0" />
          {formatDate(row.currentPeriodEnd, locale)}
        </span>
      </span>

      <span className="crm-tnum text-[13px]">{row.addOns.length || "-"}</span>

      <SubsRowMenu row={row} t={t} onAssign={onAssign} onRefund={onRefund} />
    </div>
  );
}
